import React, { useEffect, useRef, useState } from "react";

import { useForm } from "../useForm";

import AsyncSelect from "react-select/async";
import { Stepper } from "primereact/stepper";
import { StepperPanel } from "primereact/stepperpanel";

import Input from "../../controls/Input";
import SelectMenu from "../../controls/SelectMenu";
import OrderProductsTable from "./OrderProductsTable";
import DeleteConfirmationForm from "../DeleteConfirmationForm";
import DatePickerInput from "../../controls/DatePickerInput";
import AddProductToOrderForm from "./modals/AddProductToOrderForm";
import OrderNotes from "./modals/OrderNotes";
import {
  convertToDefEventPara,
  orderStatusOptions,
} from "../../../shared/utility";
import { classNames } from "primereact/utils";

const initialFValues = {
  id: 0,
  customer: "",
  customer_id: 0,
  subtotal_dollar_amount: 0,
  total_dollar_amount: 0,
  order_date: new Date(),
  products: [],
};

export default function CreateNewOrderForm(props) {
  const {
    customers,
    getMyProducts,
    employeeOptions,
    isEdit,
    order,
    setEmptyModelOpen,
    showErrorModal,
    updateOrderApiCall,
    createOrderApiCall,
    onUpdateOrderItem,
    deleteOrderItemApiCall,
    addProductToOrderApiCall,
    printOrder,
  } = props;
  const asyncRef = useRef(null);
  const stepperRef = useRef(null);
  const [asyncSelectValue, setAsyncSelectValue] = useState({
    value: -1,
    label: "",
  });

  const validate = (fieldValues = values) => {
    const temp = { ...errors };

    temp.customer = null;
    if ((fieldValues.customer ?? values.customer) === "") {
      temp.customer = "Please fill out field.";
    }

    temp.expected_date = null;
    if ((fieldValues.expected_date ?? values.expected_date) === "") {
      temp.expected_date = "Please fill out field.";
    }

    temp.po_number = null;
    if ((fieldValues.po_number ?? values.po_number) === "") {
      temp.po_number = "Please fill out field.";
    }

    temp.products = null;
    if ((fieldValues.products ?? values.products).length < 1) {
      temp.products = "Please add at least one product.";
    }

    setErrors({
      ...temp,
    });

    if (fieldValues === values) {
      return Object.values(temp).every((x) => x === null);
    }

    return Object.values(temp).every((x) => x === null);
  };

  const { values, setValues, errors, setErrors, handleInputChange } = useForm(
    initialFValues,
    false,
    validate
  );

  useEffect(() => {
    focusAsync();
  }, []);

  useEffect(() => {
    if (order) {
      const orderCopy = {
        ...order,
        status: orderStatusOptions.filter(
          (option) => option.value === order.status
        )[0],
      };
      setValues(orderCopy);
    }
  }, [order, setValues]);

  function findIndexForProduct(product_id) {
    return values.products.findIndex(
      (element) => element.product_id === product_id
    );
  }

  const addProductToOrder = async function (addedProductValues) {
    setAsyncSelectValue({ value: -1, label: "" });
    const productToAdd = {
      id: -1,
      product_name: addedProductValues.name_en,
      product_id: addedProductValues.id,
      quantity: addedProductValues.quantity,
      unit_of_measure: addedProductValues.unit_of_measure,
      packing_size: addedProductValues.weight,
      individual_unit_size: addedProductValues.individual_unit_size,
      selling_unit_of_measure: addedProductValues.selling_unit_of_measure,
      plu: addedProductValues.plu,
      description_en: addedProductValues.description_en,
    };

    const productIndex = findIndexForProduct(productToAdd.product_id);
    if (productIndex > -1) {
      showErrorModal("Product already in order, please edit it.");
      return;
    }

    let callResult = { success: false };
    let newOrderProducts = [];
    if (isEdit) {
      callResult = await addProductToOrderApiCall(values.id, productToAdd);
      newOrderProducts = [...values.products, callResult.data];
    } else {
      newOrderProducts = [...values.products, productToAdd];
    }

    if (callResult.success || !isEdit) {
      setValues({
        ...values,
        products: newOrderProducts,
      });
      setEmptyModelOpen({
        childComponent: null,
        open: false,
      });
      setErrors((currentErrors) => ({ ...currentErrors, products: null }));
    }

    setTimeout(() => {
      focusAsync();
    }, 500);
  };

  const updateOrder = () => {
    if (validate()) {
      updateOrderApiCall(values);
    }
  };

  const createOrder = () => {
    if (validate()) {
      createOrderApiCall(values);
    }
  };

  const updateItemLocally = (newData, index) => {
    const currentOrders = [...values.products];
    currentOrders[index] = newData;
    setValues({ ...values, products: currentOrders });
  };

  const withCalculatedTotal = (item) => {
    const quantity = parseFloat(item.quantity ?? 0);
    const unitPrice = parseFloat(item.unit_price ?? 0);

    if (Number.isNaN(quantity) || Number.isNaN(unitPrice)) {
      return item;
    }

    return {
      ...item,
      total_dollar_amount: (quantity * unitPrice).toFixed(2),
    };
  };

  const onItemEditComplete = async function (newData, index) {
    const nextData = withCalculatedTotal(newData);

    if (isEdit) {
      const onUpdateCompleted = await onUpdateOrderItem(
        nextData.id,
        nextData.quantity
      );

      if (onUpdateCompleted.success) {
        updateItemLocally(nextData, index);
      }
      return;
    }

    updateItemLocally(nextData, index);
  };

  const makeDeleteOrderItemApiCall = async function (itemIdInDB, product_id) {
    let onItemDeleted = { success: false };
    if (isEdit) {
      onItemDeleted = await deleteOrderItemApiCall(itemIdInDB);
    }

    if (onItemDeleted.success || !isEdit) {
      const filteredItems = values.products.filter(
        (item) => item.product_id !== product_id
      );
      setValues({ ...values, products: filteredItems });
    }
    setEmptyModelOpen({
      childComponent: null,
      open: false,
    });
  };

  const askDeleteOrderItem = function (product) {
    setEmptyModelOpen({
      childComponent: (
        <DeleteConfirmationForm
          buttonMessage="Delete"
          confirmAction={() =>
            makeDeleteOrderItemApiCall(product.id, product.product_id)
          }
          message={`Are you sure you want to ${product.product_name}?`}
          setOpen={setEmptyModelOpen}
        />
      ),
      open: true,
    });
  };

  const loadOptions = async function (input, callback) {
    if (input.length > 2) {
      const result = await getMyProducts(input, 1);
      callback(result);
    }
  };

  const handleSubmit = () => {
    if (values.id > 0) {
      updateOrder();
    } else {
      createOrder();
    }
  };

  const focusAsync = () => {
    asyncRef.current?.focus();
  };

  const validateCustomerStep = () => {
    const isValid = values.customer !== "";
    setErrors((currentErrors) => ({
      ...currentErrors,
      customer: isValid ? null : "Please fill out field.",
    }));

    return isValid;
  };

  const validateDetailsStep = () => {
    const isValid = Boolean(values.expected_date) && Boolean(values.po_number);
    setErrors((currentErrors) => ({
      ...currentErrors,
      expected_date: values.expected_date ? null : "Please fill out field.",
      po_number: values.po_number ? null : "Please fill out field.",
    }));

    return isValid;
  };

  const validateProductsStep = () => {
    const isValid = values.products.length > 0;
    setErrors((currentErrors) => ({
      ...currentErrors,
      products: isValid ? null : "Please add at least one product.",
    }));

    return isValid;
  };

  const goNext = (validator) => {
    if (validator()) {
      stepperRef.current?.nextCallback();
    }
  };

  const openNotesEditor = () => {
    setEmptyModelOpen({
      childComponent: (
        <OrderNotes
          orderNote={values.notes}
          confirmAction={(newNote) =>
            handleInputChange(convertToDefEventPara("notes", newNote))
          }
          setOpen={setEmptyModelOpen}
        />
      ),
      open: true,
    });
  };

  const renderStepNav = ({
    onBack,
    onNext,
    nextLabel = "Next",
    nextClassName = "bg-main-purple hover:bg-indigo-600 focus-visible:outline-indigo-600",
  }) => (
    <div className="mt-6 flex items-center justify-between">
      <button
        type="button"
        className={classNames(
          "rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2",
          onBack
            ? "bg-gray-600 hover:bg-gray-700 focus-visible:outline-gray-600"
            : "invisible"
        )}
        onClick={onBack}
      >
        Back
      </button>
      <button
        type="button"
        className={classNames(
          "rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2",
          nextClassName
        )}
        onClick={onNext}
      >
        {nextLabel}
      </button>
    </div>
  );

  const renderNotesCard = () => (
    <div className="card-custom p-3 mt-3">
      <div className="flex justify-between">
        <p className="text-sm text-gray-700 font-semibold">Notes</p>
        <button
          onClick={openNotesEditor}
          className="text-sm font-semibold main-purple"
        >
          Edit
        </button>
      </div>
      <p className="text-sm text-gray-700 font-normal">
        {values.notes ? values.notes : "No notes"}
      </p>
    </div>
  );

  const renderCreateStepper = () => (
    <div className="card-custom p-4">
      <Stepper ref={stepperRef} linear>
        <StepperPanel header="Customer">
          <div className="space-y-4">
            <SelectMenu
              handleInputChange={handleInputChange}
              name="customer"
              options={customers}
              defaultValue={null}
              title={"Customer"}
              error={errors.customer}
              showLabel={true}
            />
            <SelectMenu
              handleInputChange={handleInputChange}
              name="created_by"
              options={employeeOptions}
              defaultValue={null}
              title={"Employee"}
              error={errors.created_by}
              showLabel={true}
            />
          </div>
          {renderStepNav({
            onNext: () => goNext(validateCustomerStep),
          })}
        </StepperPanel>

        <StepperPanel header="Order details">
          <div className="grid gap-4 md:grid-cols-2">
            <Input
              label="PO #"
              name="po_number"
              onChange={handleInputChange}
              value={values.po_number}
              labelOn={true}
              type="text"
              error={errors.po_number}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-35-custom px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
            />

            <DatePickerInput
              label="Order Date"
              name="order_date"
              onChange={handleInputChange}
              value={values.order_date}
              labelOn={true}
              minDate={new Date()}
              dateFormat="LLL dd, yyyy"
              type="text"
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
              error={errors.order_date}
            />

            <DatePickerInput
              label="Expected Date"
              name="expected_date"
              onChange={handleInputChange}
              value={values.expected_date ? values.expected_date : null}
              labelOn={true}
              minDate={new Date()}
              dateFormat="LLL dd, yyyy"
              type="text"
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
              error={errors.expected_date}
            />
          </div>
          {renderNotesCard()}
          {renderStepNav({
            onBack: () => stepperRef.current?.prevCallback(),
            onNext: () => goNext(validateDetailsStep),
          })}
        </StepperPanel>

        <StepperPanel header="Products">
          <div className="space-y-4">
            <div className="flex rounded-md">
              <AsyncSelect
                cacheOptions
                loadOptions={loadOptions}
                defaultOptions={false}
                options={[]}
                className="w-full"
                ref={asyncRef}
                onChange={(e) => {
                  setEmptyModelOpen({
                    childComponent: (
                      <AddProductToOrderForm
                        product={e}
                        confirmAction={addProductToOrder}
                        isEdit={false}
                        setOpen={() => {
                          setEmptyModelOpen({
                            open: false,
                          });
                          setAsyncSelectValue({ value: -1, label: "" });
                        }}
                      />
                    ),
                    open: true,
                  });
                }}
                value={asyncSelectValue}
              />
            </div>

            {errors.products ? (
              <p className="font-medium text-red-500">{errors.products}</p>
            ) : null}

            <OrderProductsTable
              addProductToOrder={addProductToOrder}
              loading={false}
              values={values}
              setEmptyModelOpen={setEmptyModelOpen}
              onItemEditComplete={onItemEditComplete}
              askDeleteOrderItem={askDeleteOrderItem}
              isEdit={isEdit}
            />
          </div>
          {renderStepNav({
            onBack: () => stepperRef.current?.prevCallback(),
            onNext: () => goNext(validateProductsStep),
          })}
        </StepperPanel>

        <StepperPanel header="Review">
          <div className="grid gap-4 lg:grid-cols-3">
            <div className="lg:col-span-2 card-custom p-3">
              <OrderProductsTable
                addProductToOrder={addProductToOrder}
                loading={false}
                values={values}
                setEmptyModelOpen={setEmptyModelOpen}
                onItemEditComplete={onItemEditComplete}
                askDeleteOrderItem={askDeleteOrderItem}
                isEdit={isEdit}
              />
            </div>

            <div className="space-y-4">
              <div className="card-custom p-3 space-y-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Customer
                  </p>
                  <p className="text-sm text-gray-900">
                    {values.customer?.label || "Not selected"}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Employee
                  </p>
                  <p className="text-sm text-gray-900">
                    {values.created_by?.label || "Not selected"}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    PO Number
                  </p>
                  <p className="text-sm text-gray-900">
                    {values.po_number || "Not set"}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Expected Date
                  </p>
                  <p className="text-sm text-gray-900">
                    {values.expected_date
                      ? new Date(values.expected_date).toLocaleDateString()
                      : "Not set"}
                  </p>
                </div>
              </div>

              {renderNotesCard()}
            </div>
          </div>

          {renderStepNav({
            onBack: () => stepperRef.current?.prevCallback(),
            onNext: handleSubmit,
            nextLabel: "Create Order",
            nextClassName:
              "bg-green-600 hover:bg-green-700 focus-visible:outline-green-600",
          })}
        </StepperPanel>
      </Stepper>
    </div>
  );

  if (!isEdit) {
    return renderCreateStepper();
  }

  return (
    <div className="grid grid-cols-3 gap-3 h-full">
      <div className="col-span-2">
        <div className="card-custom p-3 py-5">
          <div className="flex rounded-md">
            <AsyncSelect
              cacheOptions
              loadOptions={loadOptions}
              defaultOptions={false}
              options={[]}
              className="w-full"
              ref={asyncRef}
              onChange={(e) => {
                setEmptyModelOpen({
                  childComponent: (
                    <AddProductToOrderForm
                      product={e}
                      confirmAction={addProductToOrder}
                      isEdit={false}
                      setOpen={() => {
                        setEmptyModelOpen({
                          open: false,
                        });
                        setAsyncSelectValue({ value: -1, label: "" });
                      }}
                    />
                  ),
                  open: true,
                });
              }}
              value={asyncSelectValue}
            />
          </div>

          <OrderProductsTable
            addProductToOrder={addProductToOrder}
            loading={false}
            values={values}
            setEmptyModelOpen={setEmptyModelOpen}
            onItemEditComplete={onItemEditComplete}
            askDeleteOrderItem={askDeleteOrderItem}
            isEdit={isEdit}
          />
        </div>
      </div>

      <div className="col-span-1 space-y-5">
        <div className="card-custom p-3 space-y-2">
          <SelectMenu
            handleInputChange={handleInputChange}
            name="customer"
            options={customers}
            defaultValue={isEdit ? order.customer : null}
            title={"Customer"}
            error={errors.customer}
            showLabel={true}
          />

          <Input
            label="PO #"
            name="po_number"
            onChange={handleInputChange}
            value={values.po_number}
            labelOn={true}
            type="text"
            error={errors.po_number}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-35-custom px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
          />

          <SelectMenu
            handleInputChange={handleInputChange}
            name="created_by"
            options={employeeOptions}
            defaultValue={isEdit ? order.created_by : null}
            title={"Employee"}
            error={errors.created_by}
            showLabel={true}
          />

          <div className="mt-3 flex flex-shrink-0 py-4">
            {typeof printOrder === "function" ? (
              <button
                type="button"
                className={classNames(
                  values.id > 0 ? "" : null,
                  "inline-flex justify-center rounded-md bg-gray-500 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600 mr-4"
                )}
                onClick={() => printOrder()}
              >
                Print
              </button>
            ) : null}
            <button
              type="submit"
              className="inline-flex justify-center rounded-md bg-main-purple px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
              onClick={handleSubmit}
            >
              {values.id > 0 ? "Save" : "Create"}
            </button>
          </div>
        </div>
        <div className="card-custom p-3 space-y-2">
          <DatePickerInput
            label="Order Date"
            name="order_date"
            onChange={handleInputChange}
            value={values.order_date}
            labelOn={true}
            minDate={new Date()}
            dateFormat="LLL dd, yyyy"
            type="text"
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
            error={errors.order_date}
          />

          <DatePickerInput
            label="Expected Date"
            name="expected_date"
            onChange={handleInputChange}
            value={values.expected_date ? values.expected_date : null}
            labelOn={true}
            minDate={new Date()}
            dateFormat="LLL dd, yyyy"
            type="text"
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
            error={errors.expected_date}
          />

          {isEdit ? (
            <SelectMenu
              handleInputChange={handleInputChange}
              name="status"
              options={orderStatusOptions.filter(
                (option) => option.value !== -1
              )}
              value={isEdit ? values.status : null}
              title={"Status"}
              error={errors.status}
              showLabel={true}
            />
          ) : null}
        </div>
        {renderNotesCard()}
      </div>
    </div>
  );
}
